import { Add, Delete, Edit } from "@mui/icons-material";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import { handleError } from "helpers/utils";
import {
  ICompanyProficiencyLevels,
  ICompanyProficiencyLevelsInput,
} from "models/companyProficiencyLevels";
import { useTranslation } from "react-i18next";
import { IPermission } from "models/permissions";
import BlockIcon from "@mui/icons-material/Block";
import { getPermission, getUserData } from "helpers";
import {
  addCompanyProficiencyLevel,
  CompanyProficiencyLevelsCells,
  deleteCompanyProficiencyLevel,
  editCompanyProficiencyLevel,
  getCompanyProficiencyLevels,
} from "framework/companyProficiencyLevel";
import EditCompanyProficiencyLevel from "./partials/EditCompanyProficiencyLevel/EditCompanyProficiencyLevel";
import useFetchSection from "hooks/useFetchSection";
import Search from "components/Search/Search";
import AddCompanyProficiencyLevel from "./partials/AddCompanyProficiencyLevel/AddCompanyProficiencyLevel";
import DeleteCompanyProficiencyLevel from "./partials/DeleteCompanyProficiencyLevel/DeleteCompanyProficiencyLevel";
interface Props {
  feature: Feature;
}

const CompanyDepartments: React.FC<Props> = ({ feature }) => {
  const [loading2, setLoading2] = useState<boolean>(false);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [companyProficiencyLevels, setCompanyProficiencyLevels] = useState<
    ICompanyProficiencyLevels[]
  >([] as ICompanyProficiencyLevels[]);

  const { t, i18n } = useTranslation();
  const [inputObj, setInputObj] = useState<ICompanyProficiencyLevelsInput>(
    {} as ICompanyProficiencyLevelsInput
  );
  const { setAlert } = useAlert();
  const user = getUserData();
  const [selectedCompanyProficiencyLevel, setSelectedCompanyProficiencyLevel] =
    useState<ICompanyProficiencyLevels>({} as ICompanyProficiencyLevels);
  const { colors } = user;
  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addMutation = useMutation({
    mutationFn: (createInput: ICompanyProficiencyLevelsInput) => {
      return addCompanyProficiencyLevel(createInput);
    },
  });

  const editMutation = useMutation({
    mutationFn: (data: {
      createInput: ICompanyProficiencyLevelsInput;
      id: string;
    }) => {
      return editCompanyProficiencyLevel(data.createInput, data.id);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteCompanyProficiencyLevel(id);
    },
  });

  const handleOpenAdd = () => setOpenAdd(true);


  const {
    loading,
    data: ProfLevel,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getCompanyProficiencyLevels(),
    fetchPermission: () => getPermission(28),
    section: "get company proficiency levels",
    onSuccess: (data) => setCompanyProficiencyLevels(data),
  });

  const handleOpenEdit = (level: ICompanyProficiencyLevels) => {
    setSelectedCompanyProficiencyLevel(level);
    setOpenEdit(true);
  };

  const handleOpenDelete = (level: ICompanyProficiencyLevels) => {
    setSelectedCompanyProficiencyLevel(level);
    setOpenDelete(true);
  };

  const handleClose = () => {
    setOpenEdit(false);
    setOpenAdd(false);
    setOpenDelete(false);
    setSelectedCompanyProficiencyLevel({} as ICompanyProficiencyLevels);
  };



  const handleAdd = async () => {

    if (!inputObj.name || !inputObj.nameAr || !inputObj.rank)
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });

    try {
      setLoading2(true);
      await addMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "Proficiency Level added successfully",
        type: "success",
      });
      revalidate();
      handleClose();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading2(true);
      let createInput: ICompanyProficiencyLevelsInput = {
        name: inputObj.name || selectedCompanyProficiencyLevel.name,
        nameAr: inputObj.nameAr || selectedCompanyProficiencyLevel.nameAr,
        rank: inputObj.rank || selectedCompanyProficiencyLevel.rank
      };
      await editMutation.mutateAsync({
        createInput,
        id: selectedCompanyProficiencyLevel.id,
      });
      setAlert({
        open: true,
        message: "Proficiency Level updated successfully",
        type: "success",
      });
      revalidate();
      handleClose();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteMutation.mutateAsync(selectedCompanyProficiencyLevel.id);
      setAlert({
        open: true,
        message: "Proficiency Level deleted successfully",
        type: "success",
      });
      revalidate();
      handleClose();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const getBodyCells = (row: ICompanyProficiencyLevels) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "name")
        return {
          id: "name",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.name}</Typography>
            </TableCell>
          ),
        };

      if (key === "nameAr")
        return {
          id: "nameAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.nameAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "rank")
        return {
          id: "rank",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.rank}</Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}

              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleSearch = async (value: string) => {
    if (!value) return setCompanyProficiencyLevels(ProfLevel!);
    let filtered = ProfLevel!.filter((item) =>
      (item.name + ' ' + item.nameAr + ' ' + item.rank).toLowerCase().includes(value.toLowerCase())
    );
    setCompanyProficiencyLevels(filtered);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />

            {permission?.hasModifyPermission && (
              <Tooltip title={t('T_add')}>
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {permission?.hasReadPermission! &&
          companyProficiencyLevels.length > 0 && (
            <List
              headCells={CompanyProficiencyLevelsCells}
              data={companyProficiencyLevels}
              getBodyCells={getBodyCells}
              feature={feature}
              canEdit={false}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}

        {(!companyProficiencyLevels || companyProficiencyLevels.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_prof_header")}
              message={t("T_no_prof_message")}
            />
          )}
        {openAdd && (
          <AddCompanyProficiencyLevel
            open={openAdd}
            loading={loading2}
            onClose={handleClose}
            onSubmit={handleAdd}
            onChanges={handleChange}
          />
        )}

        {openEdit && (
          <EditCompanyProficiencyLevel
            open={openEdit}
            loading={loading2}
            companyProficiencyLevel={selectedCompanyProficiencyLevel}
            onClose={handleClose}
            onSubmit={handleEdit}
            onChanges={handleChange}
          />
        )}

        {openDelete && (
          <DeleteCompanyProficiencyLevel
            open={openDelete}
            loading={loading2}
            onClose={handleClose}
            onSubmit={handleDelete}
          />
        )}
      </Stack>
    </>
  );
};

export default CompanyDepartments;
